import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { captureException } from '@playbookux/playbook-web-core';

import ProjectComponent from '../project';
import { Loader } from './styles';
import ExportDialog from '../../../../../core/components/export-project-dialog';
import {
  exportModeratedProject,
  exportUnmoderatedProject,
  exportCardSort,
  exportTreeTest,
  exportSurveyProject,
  exportUploadProject,
  downloadExportProject,
} from '../../../../../core/services/export';
import TestTypes from '../../../../../core/constants/testTypes';
import { registerFunctions } from '../../../../../core/hooks/use-function-handler';

const ProjectsList = (props) => {
  const {
    loading,
    refetch,
    handlingLoadingProject,
    handleUpdateCompany,
    attributes,
    company,
    setProjectModal,
    showModalShareProject,
    setShowModalShareProject,
    loadingProject,
    projectsList,
  } = props;

  /**
   * EXPORT PROJECT
   */

  const [showExport, setShowExport] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorExport, setErrorExport] = useState(false);
  const [exportBlob, setExportBlob] = useState({});

  // we need to show that the export in process of building
  const haveMessageFromServer = async (blobInfo) => {
    try {
      const text = await blobInfo.blob.text();
      const parsedText = JSON.parse(text)?.exportStatus;

      return parsedText;
    } catch {
      return '';
    }
  };

  const handleExport = async (testType, projectId, projectName) => {
    try {
      let blobInfo = {};
      setShowExport(true);
      setLoadingExport(true);
      switch (testType) {
        case TestTypes.MODERATED:
          blobInfo = await exportModeratedProject(projectId, false);
          break;

        case TestTypes.UNMODERATED:
          blobInfo = await exportUnmoderatedProject(projectId, false);
          break;

        case TestTypes.CARD_SORT:
          blobInfo = await exportCardSort(projectId, false);
          break;

        case TestTypes.TREE_TEST:
          blobInfo = await exportTreeTest(projectId, projectName, false);
          break;

        case TestTypes.SURVEY:
          blobInfo = await exportSurveyProject(projectId, projectName, false);
          break;
        case TestTypes.UPLOAD:
          blobInfo = await exportUploadProject(projectId, false);
          break;
        default:
          break;
      }
      const isInProgress = await haveMessageFromServer(blobInfo);
      if (isInProgress === 'in_progress') {
        return;
      }
      setExportBlob(blobInfo);
      setLoadingExport(false);
    } catch (err) {
      setLoadingExport(false);
      setErrorExport(true);
      captureException('HANDLE_EXPORT_FAILED', err);
    }
  };

  // when receiving message from websocket, this function is called because the export is completed or failed
  const handleExportCompleted = (exportCompleted) => {
    if (exportCompleted?.error === false && showExport) {
      const { data } = exportCompleted;
      handleExport(data?.test_type, data?.project_id, data?.project_name);
    } else if (exportCompleted?.error === true) {
      setLoadingExport(false);
      setErrorExport(true);
      captureException('HANDLE_EXPORT_FAILED', exportCompleted);
    }
  };

  registerFunctions('dashboard_export_completed', {
    func1: handleExportCompleted,
  });

  const handleDownloadExport = async () => {
    try {
      await downloadExportProject(exportBlob);
      setShowExport(false);
    } catch (err) {
      captureException('HANDLE_EXPORT_FAILED', err);
    }
  };

  const handleCloseExport = () => {
    setShowExport(false);
    setErrorExport(false);
  };

  /**
   * END EXPORT PROJECT
   */

  return (
    <>
      <ExportDialog
        show={showExport}
        loadingExport={loadingExport}
        downloadExport={handleDownloadExport}
        handleClose={handleCloseExport}
        errorExport={errorExport}
      />
      {(loading || loadingProject) && <Loader data-loader="project-list-loader" />}
      {!loading && _.map(projectsList, (project, index) => (
        <ProjectComponent
          key={project.id}
          project={project}
          onRefreshProjects={refetch}
          onSetLoadingProject={handlingLoadingProject}
          onRemoveDemoProject={handleUpdateCompany}
          company={company}
          setProjectModal={setProjectModal}
          showModalShareProject={showModalShareProject}
          setShowModalShareProject={setShowModalShareProject}
          queryAttributes={attributes}
          onHandleExport={handleExport}
          index={index}
        />
      ))}
    </>
  );
};

const companyType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  plan: PropTypes.string,
  stripeCustomerId: PropTypes.string,
});

ProjectsList.propTypes = {
  loading: PropTypes.bool.isRequired,
  refetch: PropTypes.func.isRequired,
  handlingLoadingProject: PropTypes.func.isRequired,
  handleUpdateCompany: PropTypes.func.isRequired,
  attributes: PropTypes.shape().isRequired,
  company: companyType.isRequired,
  setProjectModal: PropTypes.func.isRequired,
  showModalShareProject: PropTypes.bool.isRequired,
  setShowModalShareProject: PropTypes.func.isRequired,
  loadingProject: PropTypes.bool.isRequired,
  projectsList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default ProjectsList;
